import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";

import type { BaseBlockFilterModel } from "shared/types/FilterModel";

import type { FilterBlockModel } from "./FilterBlock";
import FilterBlock, { FilterBlockTypes } from "./FilterBlock";

export interface FilterBlocksMethods {
	getSelectedOptions: () => BaseBlockFilterModel[];
	clearFilters: () => void;
}

export interface FilterBlocksProps {
	blocks: FilterBlockModel[];
	defaultFiltersData: BaseBlockFilterModel[];
}

const FilterBlocks = forwardRef<FilterBlocksMethods, FilterBlocksProps>(({ blocks, defaultFiltersData }, ref) => {
	type FilterBlockMethodsHandlers = React.ElementRef<typeof FilterBlock>;
	const filterBlockRef = useRef<(FilterBlockMethodsHandlers | null)[]>([]);

	useEffect(() => {
		if (filterBlockRef.current.length && defaultFiltersData.length) {
			defaultFiltersData.map(fd => {
				const filter = filterBlockRef.current.find(x => x?.getBlockId() === fd.blockId);
				if (filter) {
					switch (filter.getType()) {
						case FilterBlockTypes.INPUT:
							filter.setInputText(fd.inputText || "");
							break;

						case FilterBlockTypes.SWITCHER:
							filter.setChecked(fd.checked || false);
							break;

						case FilterBlockTypes.CHECKBOX:
							filter.setSelectedOptions(fd.values || []);
							break;

						case FilterBlockTypes.RADIO:
							filter.setRadioValue(fd.radioValue || "");
							break;
					}
				}
			});
		}
	}, [defaultFiltersData]);

	useImperativeHandle(ref, () => ({
		getSelectedOptions: () => {
			if (filterBlockRef.current.length) {
				return filterBlockRef.current.map(block => ({
					blockId: block!.getBlockId(),
					type: block!.getType(),
					values: block!.getSelectedOptions(),
					inputText: block!.getInputText(),
					checked: block!.getChecked(),
					radioValue: block!.getRadioValue()
				}));
			}

			return [];
		},
		clearFilters: () => {
			filterBlockRef.current.forEach(block => block!.clearFilter());
		}
	}));

	return (
		<div className="flex flex-col">
			{blocks.map((block, index) => (
				<div key={`filter-block-${index}`} className="border-b border-gray-100">
					<FilterBlock
						ref={el => (filterBlockRef.current ? (filterBlockRef.current[index] = el) : null)}
						block={block}
						index={index}
						defaultFilterInfo={defaultFiltersData.find(x => x.blockId === block.id)}
					/>
				</div>
			))}
		</div>
	);
});

export default FilterBlocks;
